import { useCallback } from "react";
import { useIntercom } from "react-use-intercom";
import type { ConsolidatedOrderingJourneySource } from "./useMarketStorefrontSessionTracking";

export type ConsolidatedOrderingIntercomSource =
  | "market"
  | "delivered_by_sellar_page"
  | "my_suppliers";
export type ConsolidatedOrderingStatus =
  | "searching"
  | "building_basket"
  | "checkout"
  | "partially_completed"
  | "completed";
export type ConsolidatedOrderingStep =
  | "search"
  | "basket"
  | "overview"
  | "details"
  | "pay"
  | "success";

export type ConsolidatedOrderingBasketSnapshot = {
  sessionId?: string;
  journeySource: ConsolidatedOrderingJourneySource;
  supplierCount: number;
  itemCount: number;
  paidSupplierCount?: number;
  failedSupplierCount?: number;
};

const STORAGE_KEY = "consolidated-ordering-intercom";
const PRODUCT_ADDED_KEY_PREFIX = "consolidated-ordering-product-added";
const STEP_RANK: Record<ConsolidatedOrderingStep, number> = {
  search: 0,
  basket: 1,
  overview: 2,
  details: 3,
  pay: 4,
  success: 5,
};

type StoredState = {
  emittedKeys: string[];
  furthestSteps: Record<string, ConsolidatedOrderingStep>;
};

const getStoredState = (): StoredState => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored
      ? (JSON.parse(stored) as StoredState)
      : { emittedKeys: [], furthestSteps: {} };
  } catch {
    return { emittedKeys: [], furthestSteps: {} };
  }
};

const setStoredState = (state: StoredState) => {
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
};

const emitOnce = (key: string, emit: () => void) => {
  const state = getStoredState();
  if (state.emittedKeys.includes(key)) return;
  emit();
  setStoredState({
    ...state,
    emittedKeys: [...state.emittedKeys.slice(-199), key],
  });
};

export const hasAddedConsolidatedOrderingProduct = (sessionId?: string) =>
  sessionId
    ? window.sessionStorage.getItem(
        `${PRODUCT_ADDED_KEY_PREFIX}:${sessionId}`,
      ) === "true"
    : false;

export const mapConsolidatedOrderingIntercomSource = (
  source: ConsolidatedOrderingJourneySource,
): ConsolidatedOrderingIntercomSource | undefined => {
  switch (source) {
    case "market":
      return "market";
    case "suppliers-list":
      return "my_suppliers";
    case "direct":
      return "delivered_by_sellar_page";
    case "unknown":
      return undefined;
  }
};

export const useConsolidatedOrderingIntercom = () => {
  const { trackEvent, update } = useIntercom();
  const canTrack = import.meta.env.VITE_ENV === "production";

  const updateJourney = useCallback(
    ({
      journeyKey,
      status,
      step,
      snapshot,
    }: {
      journeyKey: string;
      status: ConsolidatedOrderingStatus;
      step: ConsolidatedOrderingStep;
      snapshot: ConsolidatedOrderingBasketSnapshot;
    }) => {
      if (!canTrack) return;
      const state = getStoredState();
      const currentStep = state.furthestSteps[journeyKey];
      const furthestStep =
        !currentStep || STEP_RANK[step] > STEP_RANK[currentStep]
          ? step
          : currentStep;
      setStoredState({
        ...state,
        furthestSteps: { ...state.furthestSteps, [journeyKey]: furthestStep },
      });

      const source = mapConsolidatedOrderingIntercomSource(
        snapshot.journeySource,
      );
      update({
        customAttributes: {
          consolidated_ordering_status: status,
          consolidated_ordering_last_activity_at: Math.floor(Date.now() / 1000),
          consolidated_ordering_furthest_step: furthestStep,
          ...(source ? { consolidated_ordering_source: source } : {}),
          consolidated_ordering_supplier_count: snapshot.supplierCount,
          consolidated_ordering_item_count: snapshot.itemCount,
          consolidated_ordering_paid_supplier_count:
            snapshot.paidSupplierCount ?? 0,
          consolidated_ordering_failed_supplier_count:
            snapshot.failedSupplierCount ?? 0,
        },
      });
    },
    [canTrack, update],
  );

  const trackBasketUpdated = useCallback(
    ({
      journeyKey,
      snapshot,
      metadata,
    }: {
      journeyKey: string;
      snapshot: ConsolidatedOrderingBasketSnapshot;
      metadata: Record<string, string | number>;
    }) => {
      if (!canTrack) return;
      if (snapshot.sessionId) {
        window.sessionStorage.setItem(
          `${PRODUCT_ADDED_KEY_PREFIX}:${snapshot.sessionId}`,
          "true",
        );
      }
      updateJourney({
        journeyKey,
        status: "building_basket",
        step: "basket",
        snapshot,
      });
      trackEvent("consolidated_ordering_basket_updated", {
        ...(snapshot.sessionId ? { sessionId: snapshot.sessionId } : {}),
        supplierCount: snapshot.supplierCount,
        itemCount: snapshot.itemCount,
        ...metadata,
      });
    },
    [canTrack, trackEvent, updateJourney],
  );

  const trackCheckoutStep = useCallback(
    ({
      journeyKey,
      step,
      snapshot,
      deliveryPreference,
    }: {
      journeyKey: string;
      step: "overview" | "details" | "pay" | "success";
      snapshot: ConsolidatedOrderingBasketSnapshot;
      deliveryPreference?: "consolidated" | "split";
    }) => {
      if (!canTrack) return;
      const status = step === "success" ? "completed" : "checkout";
      updateJourney({ journeyKey, status, step, snapshot });

      if (step === "overview") {
        emitOnce(`${journeyKey}:checkout-started`, () =>
          trackEvent("consolidated_ordering_checkout_started", {
            ...(snapshot.sessionId ? { sessionId: snapshot.sessionId } : {}),
            supplierCount: snapshot.supplierCount,
            itemCount: snapshot.itemCount,
          }),
        );
      }

      emitOnce(`${journeyKey}:step:${step}`, () =>
        trackEvent("consolidated_ordering_checkout_step_viewed", {
          ...(snapshot.sessionId ? { sessionId: snapshot.sessionId } : {}),
          step,
          supplierCount: snapshot.supplierCount,
          itemCount: snapshot.itemCount,
          paidSupplierCount: snapshot.paidSupplierCount ?? 0,
          failedSupplierCount: snapshot.failedSupplierCount ?? 0,
          ...(deliveryPreference ? { deliveryPreference } : {}),
        }),
      );
    },
    [canTrack, trackEvent, updateJourney],
  );

  const trackCompleted = useCallback(
    ({
      journeyKey,
      snapshot,
      orderCount,
      deliveryPreference,
    }: {
      journeyKey: string;
      snapshot: ConsolidatedOrderingBasketSnapshot;
      orderCount: number;
      deliveryPreference?: "consolidated" | "split";
    }) => {
      if (!canTrack) return;
      updateJourney({
        journeyKey,
        status: "completed",
        step: "success",
        snapshot,
      });
      emitOnce(`${journeyKey}:completed`, () =>
        trackEvent("consolidated_ordering_completed", {
          ...(snapshot.sessionId ? { sessionId: snapshot.sessionId } : {}),
          supplierCount: snapshot.supplierCount,
          orderCount,
          itemCount: snapshot.itemCount,
          paidSupplierCount: snapshot.paidSupplierCount ?? 0,
          failedSupplierCount: snapshot.failedSupplierCount ?? 0,
          ...(deliveryPreference ? { deliveryPreference } : {}),
          ...(mapConsolidatedOrderingIntercomSource(snapshot.journeySource)
            ? {
                source: mapConsolidatedOrderingIntercomSource(
                  snapshot.journeySource,
                ),
              }
            : {}),
        }),
      );
    },
    [canTrack, trackEvent, updateJourney],
  );

  return {
    trackEvent,
    trackEventOnce: (key: string, event: string, metadata: object) => {
      if (!canTrack) return;
      emitOnce(key, () => trackEvent(event, metadata));
    },
    updateJourney,
    trackBasketUpdated,
    trackCheckoutStep,
    trackCompleted,
  };
};
